import {
  Activity,
  AlertTriangle,
  ArrowDownUp,
  Boxes,
  Check,
  ChevronLeft,
  CircleDot,
  CloudCog,
  Database,
  GitBranch,
  HardDrive,
  LayoutDashboard,
  MemoryStick,
  RefreshCw,
  RotateCcw,
  Search,
  Server,
  ShieldAlert,
  Timer,
} from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { createDemoRepository, filterByEnvironment } from "./data/repository";
import type {
  DatabaseInstance,
  DemoData,
  Deployment,
  Environment,
  Health,
  Incident,
  Migration,
  PipelineStep,
  RunStatus,
  Scenario,
} from "./types";

type Page =
  "dashboard" | "instances" | "incidents" | "deployments" | "migrations";

const scenarios: { value: Scenario; label: string }[] = [
  { value: "mixed", label: "混合維運狀態" },
  { value: "healthy", label: "全部健康" },
  { value: "degraded", label: "服務效能下降" },
  { value: "incident", label: "發生中的事件" },
  { value: "deployment-failure", label: "部署失敗" },
  { value: "empty", label: "無資料狀態" },
  { value: "error", label: "資料來源錯誤" },
];

const navigation = [
  { page: "dashboard" as const, label: "儀表板", icon: LayoutDashboard },
  { page: "instances" as const, label: "資料庫實例", icon: Database },
  { page: "incidents" as const, label: "事件", icon: ShieldAlert },
  { page: "deployments" as const, label: "CI/CD", icon: CloudCog },
  { page: "migrations" as const, label: "資料庫遷移", icon: GitBranch },
];

const statusLabels: Record<
  Health | RunStatus | Incident["status"] | PipelineStep["status"],
  string
> = {
  Healthy: "健康",
  Warning: "警告",
  Critical: "嚴重",
  Queued: "排隊中",
  Running: "執行中",
  Succeeded: "成功",
  Failed: "失敗",
  Cancelled: "已取消",
  "Rolled Back": "已回復",
  Pending: "等待中",
  Skipped: "已略過",
  Open: "待處理",
  Acknowledged: "已確認",
  Resolved: "已解決",
};

const environmentLabels: Record<
  Environment | Deployment["environment"],
  string
> = {
  All: "全部環境",
  Dev: "開發",
  Staging: "預備",
  Production: "正式",
  Preview: "預覽",
};

const buildMetadata = {
  version: import.meta.env.VITE_APP_VERSION || "local",
  sha: import.meta.env.VITE_GIT_SHA || "working-tree",
  time: import.meta.env.VITE_BUILD_TIME || "local build",
  environment: import.meta.env.VITE_ENVIRONMENT || "development",
};

const buildEnvironmentLabels: Record<string, string> = {
  development: "開發環境",
  preview: "預覽環境",
  production: "正式環境",
  "production-rollback": "正式環境回復",
};

function getRoute(): { page: Page; detailId?: string } {
  const route = window.location.hash
    .replace(/^#\/?/, "")
    .split("/")
    .filter(Boolean);
  const page = navigation.some((item) => item.page === route[0])
    ? (route[0] as Page)
    : "dashboard";
  return { page, detailId: page === "instances" ? route[1] : undefined };
}

function statusClass(
  status: Health | RunStatus | Incident["status"] | PipelineStep["status"],
): string {
  if (status === "Healthy" || status === "Succeeded" || status === "Resolved")
    return "bg-emerald-50 text-emerald-800 border-emerald-200";
  if (status === "Warning" || status === "Running" || status === "Acknowledged")
    return "bg-amber-50 text-amber-800 border-amber-200";
  if (status === "Critical" || status === "Failed")
    return "bg-red-50 text-red-800 border-red-200";
  if (status === "Rolled Back")
    return "bg-violet-50 text-violet-800 border-violet-200";
  return "bg-zinc-100 text-zinc-700 border-zinc-200";
}

function StatusBadge({
  status,
}: {
  status: Health | RunStatus | Incident["status"] | PipelineStep["status"];
}) {
  return (
    <span
      className={`inline-flex whitespace-nowrap rounded border px-2 py-0.5 text-xs font-semibold ${statusClass(status)}`}
    >
      {statusLabels[status]}
    </span>
  );
}

function EmptyState({ title, message }: { title: string; message: string }) {
  return (
    <div className="panel flex min-h-64 flex-col items-center justify-center px-6 text-center">
      <Database className="mb-3 h-9 w-9 text-zinc-300" />
      <h2 className="text-base font-semibold">{title}</h2>
      <p className="mt-1 max-w-md text-sm text-zinc-500">{message}</p>
    </div>
  );
}

function SectionHeader({
  title,
  subtitle,
  action,
}: {
  title: string;
  subtitle: string;
  action?: React.ReactNode;
}) {
  return (
    <div className="mb-4 flex flex-wrap items-end justify-between gap-3">
      <div>
        <h1 className="text-xl font-bold text-zinc-900">{title}</h1>
        <p className="mt-1 text-sm text-zinc-500">{subtitle}</p>
      </div>
      {action}
    </div>
  );
}

function Metric({
  label,
  value,
  note,
  icon: Icon,
}: {
  label: string;
  value: string;
  note: string;
  icon: React.ComponentType<{ className?: string }>;
}) {
  return (
    <div className="panel min-w-0 p-4">
      <div className="flex items-center justify-between text-xs font-semibold uppercase text-zinc-500">
        <span>{label}</span>
        <Icon className="h-4 w-4" />
      </div>
      <p className="mt-3 truncate text-2xl font-bold text-zinc-900">{value}</p>
      <p className="mt-1 text-xs text-zinc-500">{note}</p>
    </div>
  );
}

function TrendChart({ instances }: { instances: DatabaseInstance[] }) {
  const values = instances[0]?.trend ?? [];
  const points = values
    .map((point, index) => `${16 + index * 28},${126 - point.value}`)
    .join(" ");
  return (
    <div className="panel p-4">
      <div className="mb-3 flex items-center justify-between">
        <div>
          <h2 className="font-semibold">CPU 趨勢</h2>
          <p className="text-xs text-zinc-500">
            第一個可見實例最近 24 小時使用率
          </p>
        </div>
        <span className="text-sm font-semibold text-emerald-700">
          目前 {instances[0]?.cpu ?? 0}%
        </span>
      </div>
      <svg
        className="h-40 w-full"
        viewBox="0 0 680 150"
        role="img"
        aria-label="CPU 使用率趨勢"
      >
        <title>CPU 使用率趨勢</title>
        <g stroke="#e4e4e7" strokeWidth="1">
          {[30, 60, 90, 120].map((y) => (
            <line key={y} x1="16" x2="664" y1={y} y2={y} />
          ))}
        </g>
        {points && (
          <>
            <polyline
              fill="none"
              stroke="#087f5b"
              strokeWidth="3"
              points={points}
            />
            {values.map((point, index) => (
              <circle
                key={point.label}
                cx={16 + index * 28}
                cy={126 - point.value}
                r="2.5"
                fill="#087f5b"
              >
                <title>
                  {point.label}: {point.value}%
                </title>
              </circle>
            ))}
          </>
        )}
      </svg>
    </div>
  );
}

function Dashboard({
  data,
  environment,
}: {
  data: DemoData;
  environment: Environment;
}) {
  const instances = filterByEnvironment(data.instances, environment);
  const incidents = filterByEnvironment(data.incidents, environment);
  if (!instances.length)
    return (
      <EmptyState
        title="沒有資料庫監控資料"
        message="請切換環境或示範情境以載入維運儀表板資料。"
      />
    );
  const avg = (key: "cpu" | "memory" | "storage") =>
    Math.round(
      instances.reduce((sum, item) => sum + item[key], 0) / instances.length,
    );
  const critical = instances.filter(
    (item) => item.health === "Critical",
  ).length;
  return (
    <>
      <SectionHeader
        title="資料庫健康狀態"
        subtitle="所選資料庫環境的即時維運狀態。"
      />
      <div className="metric-grid grid gap-3">
        <Metric
          label="實例數量"
          value={String(instances.length)}
          note={`${critical} 個嚴重異常`}
          icon={Server}
        />
        <Metric
          label="CPU"
          value={`${avg("cpu")}%`}
          note="全部實例平均值"
          icon={Activity}
        />
        <Metric
          label="記憶體"
          value={`${avg("memory")}%`}
          note="全部實例平均值"
          icon={MemoryStick}
        />
        <Metric
          label="儲存空間"
          value={`${avg("storage")}%`}
          note="已配置容量"
          icon={HardDrive}
        />
        <Metric
          label="連線數"
          value={instances
            .reduce((s, i) => s + i.connections, 0)
            .toLocaleString()}
          note="使用中的連線階段"
          icon={CircleDot}
        />
        <Metric
          label="慢查詢"
          value={String(instances.reduce((s, i) => s + i.slowQueries, 0))}
          note="最近 15 分鐘"
          icon={Timer}
        />
      </div>
      <div className="mt-4 grid gap-4 xl:grid-cols-[1.5fr_1fr]">
        <TrendChart instances={instances} />
        <div className="panel p-4">
          <div className="mb-3 flex items-center justify-between">
            <h2 className="font-semibold">目前警示</h2>
            <span className="text-xs text-zinc-500">
              {incidents.filter((i) => i.status !== "Resolved").length} 個未結案
            </span>
          </div>
          <div className="space-y-3">
            {incidents.slice(0, 4).map((item) => (
              <div
                key={item.id}
                className="border-b border-zinc-100 pb-3 last:border-0"
              >
                <div className="flex items-start justify-between gap-3">
                  <p className="text-sm font-medium">{item.title}</p>
                  <StatusBadge status={item.severity} />
                </div>
                <p className="mt-1 font-mono text-xs text-zinc-500">
                  {item.signal}
                </p>
              </div>
            ))}
            {!incidents.length && (
              <p className="py-12 text-center text-sm text-zinc-500">
                目前沒有警示。
              </p>
            )}
          </div>
        </div>
      </div>
    </>
  );
}

function Instances({
  data,
  environment,
  detailId,
}: {
  data: DemoData;
  environment: Environment;
  detailId?: string;
}) {
  const [query, setQuery] = useState("");
  const [engine, setEngine] = useState("All");
  const [sort, setSort] = useState<"name" | "cpu">("name");
  const visible = useMemo(
    () =>
      filterByEnvironment(data.instances, environment)
        .filter(
          (item) =>
            (engine === "All" || item.engine === engine) &&
            `${item.name} ${item.endpoint}`
              .toLowerCase()
              .includes(query.toLowerCase()),
        )
        .sort((a, b) =>
          sort === "name" ? a.name.localeCompare(b.name) : b.cpu - a.cpu,
        ),
    [data.instances, environment, engine, query, sort],
  );
  const detail = data.instances.find((item) => item.id === detailId);
  if (detailId && detail)
    return (
      <>
        <a
          href="#/instances"
          className="mb-4 inline-flex items-center gap-1 text-sm font-semibold text-emerald-700"
        >
          <ChevronLeft className="h-4 w-4" />
          所有實例
        </a>
        <SectionHeader
          title={detail.name}
          subtitle={`${detail.engine} ${detail.version} · ${environmentLabels[detail.environment]}`}
          action={<StatusBadge status={detail.health} />}
        />
        <div className="grid gap-4 lg:grid-cols-[1.2fr_1fr]">
          <div className="panel p-5">
            <h2 className="font-semibold">連線資訊</h2>
            <dl className="mt-4 grid gap-4 text-sm sm:grid-cols-2">
              {[
                ["端點", detail.endpoint],
                ["區域", detail.region],
                ["角色", detail.role],
                ["負責團隊", detail.owner],
                ["運作時間", `${detail.uptimeDays} 天`],
                ["複寫延遲", `${detail.replicationLagMs} ms`],
              ].map(([k, v]) => (
                <div key={k}>
                  <dt className="text-xs uppercase text-zinc-500">{k}</dt>
                  <dd className="mt-1 break-all font-medium">{v}</dd>
                </div>
              ))}
            </dl>
          </div>
          <div className="panel p-5">
            <h2 className="font-semibold">目前負載</h2>
            {[
              ["CPU", detail.cpu],
              ["記憶體", detail.memory],
              ["儲存空間", detail.storage],
              [
                "連線數",
                Math.round((detail.connections / detail.connectionLimit) * 100),
              ],
            ].map(([k, v]) => (
              <div key={k} className="mt-4">
                <div className="mb-1 flex justify-between text-sm">
                  <span>{k}</span>
                  <b>{v}%</b>
                </div>
                <div className="h-2 bg-zinc-100">
                  <div
                    className="h-2 bg-emerald-600"
                    style={{ width: `${v}%` }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className="mt-4">
          <TrendChart instances={[detail]} />
        </div>
      </>
    );
  return (
    <>
      <SectionHeader
        title="資料庫實例"
        subtitle={`共有 ${visible.length} 個符合條件的受管實例。`}
        action={
          <div className="flex gap-2">
            <select
              aria-label="資料庫引擎篩選"
              value={engine}
              onChange={(e) => setEngine(e.target.value)}
              className="rounded border border-zinc-300 bg-white px-3 py-2 text-sm"
            >
              <option value="All">全部引擎</option>
              <option>PostgreSQL</option>
              <option>MySQL</option>
              <option>Redis</option>
            </select>
            <button
              title="變更排序"
              aria-label="變更排序"
              onClick={() => setSort(sort === "name" ? "cpu" : "name")}
              className="rounded border border-zinc-300 bg-white p-2"
            >
              <ArrowDownUp className="h-4 w-4" />
            </button>
          </div>
        }
      />
      <div className="panel mb-3 flex items-center gap-2 px-3 py-2">
        <Search className="h-4 w-4 text-zinc-400" />
        <input
          aria-label="搜尋資料庫實例"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="搜尋名稱或端點"
          className="w-full border-0 bg-transparent text-sm outline-none"
        />
      </div>
      {visible.length ? (
        <div className="panel table-scroll overflow-x-auto">
          <table className="w-full min-w-[820px] text-left text-sm">
            <thead className="border-b bg-zinc-50 text-xs uppercase text-zinc-500">
              <tr>
                {[
                  "實例",
                  "引擎",
                  "環境",
                  "健康狀態",
                  "CPU",
                  "記憶體",
                  "連線數",
                  "延遲",
                ].map((h) => (
                  <th key={h} className="px-4 py-3">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {visible.map((item) => (
                <tr
                  key={item.id}
                  className="border-b border-zinc-100 last:border-0 hover:bg-zinc-50"
                >
                  <td className="px-4 py-3">
                    <a
                      className="font-semibold text-emerald-700"
                      href={`#/instances/${item.id}`}
                    >
                      {item.name}
                    </a>
                    <div className="text-xs text-zinc-500">{item.region}</div>
                  </td>
                  <td className="px-4 py-3">
                    {item.engine} {item.version}
                  </td>
                  <td className="px-4 py-3">
                    {environmentLabels[item.environment]}
                  </td>
                  <td className="px-4 py-3">
                    <StatusBadge status={item.health} />
                  </td>
                  <td className="px-4 py-3 font-mono">{item.cpu}%</td>
                  <td className="px-4 py-3 font-mono">{item.memory}%</td>
                  <td className="px-4 py-3 font-mono">
                    {item.connections}/{item.connectionLimit}
                  </td>
                  <td className="px-4 py-3 font-mono">
                    {item.replicationLagMs} ms
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <EmptyState
          title="找不到符合條件的實例"
          message="請清除搜尋條件，或選擇其他引擎與環境。"
        />
      )}
    </>
  );
}

function Incidents({
  data,
  environment,
}: {
  data: DemoData;
  environment: Environment;
}) {
  const [items, setItems] = useState(data.incidents);
  const [status, setStatus] = useState("All");
  const visible = filterByEnvironment(items, environment).filter(
    (i) => status === "All" || i.status === status,
  );
  const update = (id: string, next: Incident["status"]) =>
    setItems((current) =>
      current.map((item) =>
        item.id === id ? { ...item, status: next } : item,
      ),
    );
  return (
    <>
      <SectionHeader
        title="事件管理"
        subtitle="確認並處理資料庫維運訊號。"
        action={
          <select
            aria-label="事件狀態篩選"
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            className="rounded border border-zinc-300 bg-white px-3 py-2 text-sm"
          >
            <option value="All">全部狀態</option>
            <option value="Open">待處理</option>
            <option value="Acknowledged">已確認</option>
            <option value="Resolved">已解決</option>
          </select>
        }
      />
      {visible.length ? (
        <div className="space-y-3">
          {visible.map((item) => (
            <article key={item.id} className="panel p-4">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <div className="flex flex-wrap items-center gap-2">
                    <StatusBadge status={item.severity} />
                    <StatusBadge status={item.status} />
                    <span className="font-mono text-xs text-zinc-400">
                      {item.id}
                    </span>
                  </div>
                  <h2 className="mt-2 font-semibold">{item.title}</h2>
                  <p className="mt-1 font-mono text-xs text-zinc-500">
                    {item.signal} · {item.instanceId} · {item.owner}
                  </p>
                </div>
                <div className="flex gap-2">
                  {item.status === "Open" && (
                    <button
                      onClick={() => update(item.id, "Acknowledged")}
                      className="inline-flex items-center gap-1 rounded border border-zinc-300 px-3 py-2 text-sm font-semibold"
                    >
                      <Check className="h-4 w-4" />
                      確認事件
                    </button>
                  )}
                  {item.status !== "Resolved" && (
                    <button
                      onClick={() => update(item.id, "Resolved")}
                      className="inline-flex items-center gap-1 rounded bg-zinc-900 px-3 py-2 text-sm font-semibold text-white"
                    >
                      <Check className="h-4 w-4" />
                      解決事件
                    </button>
                  )}
                </div>
              </div>
            </article>
          ))}
        </div>
      ) : (
        <EmptyState
          title="沒有事件"
          message="目前沒有符合所選狀態與環境的事件。"
        />
      )}
    </>
  );
}

function Steps({ steps }: { steps: PipelineStep[] }) {
  return (
    <ol className="grid gap-2 md:grid-cols-3 xl:grid-cols-9">
      {steps.map((step, index) => (
        <li
          key={step.name}
          className="relative border-l-2 border-zinc-200 pl-3"
        >
          <span className="text-xs text-zinc-400">{index + 1}</span>
          <p className="min-h-10 text-xs font-semibold">{step.name}</p>
          <StatusBadge status={step.status} />
          <p className="mt-1 font-mono text-[11px] text-zinc-400">
            {step.durationSeconds} 秒
          </p>
        </li>
      ))}
    </ol>
  );
}

function DeploymentCard({ item }: { item: Deployment }) {
  return (
    <article className="panel p-4">
      <div className="flex flex-wrap justify-between gap-3 border-b border-zinc-100 pb-4">
        <div>
          <div className="flex flex-wrap items-center gap-2">
            <StatusBadge status={item.status} />
            <span className="text-xs font-semibold uppercase text-zinc-500">
              {environmentLabels[item.environment]}
            </span>
            <span className="font-mono text-xs text-zinc-500">
              {item.commit}
            </span>
          </div>
          <h2 className="mt-2 font-semibold">{item.message}</h2>
          <p className="mt-1 text-xs text-zinc-500">
            {item.branch} · {item.version} · {item.durationSeconds} 秒 ·{" "}
            {item.actor}
          </p>
        </div>
        <a
          href={item.url}
          target="_blank"
          rel="noreferrer"
          className="text-sm font-semibold text-emerald-700"
        >
          部署網址
        </a>
      </div>
      <div className="pt-4">
        <Steps steps={item.steps} />
      </div>
    </article>
  );
}

function Deployments({ data }: { data: DemoData }) {
  return (
    <>
      <SectionHeader
        title="CI/CD 交付流程"
        subtitle="建置、安全檢查、部署與冒煙測試的執行證據。"
      />
      {data.deployments.length ? (
        <div className="space-y-3">
          {data.deployments.map((item) => (
            <DeploymentCard key={item.id} item={item} />
          ))}
        </div>
      ) : (
        <EmptyState
          title="沒有部署紀錄"
          message="所選示範情境中沒有交付歷程。"
        />
      )}
    </>
  );
}

function MigrationCard({ item }: { item: Migration }) {
  return (
    <article className="panel p-4">
      <div className="flex flex-wrap justify-between gap-3 border-b border-zinc-100 pb-4">
        <div>
          <div className="flex flex-wrap items-center gap-2">
            <StatusBadge status={item.status} />
            <span className="text-xs uppercase text-zinc-500">
              {environmentLabels[item.environment]}
            </span>
            <span className="font-mono text-xs text-zinc-500">
              {item.commit}
            </span>
          </div>
          <h2 className="mt-2 break-all font-mono text-sm font-semibold">
            {item.name}
          </h2>
          <p className="mt-1 text-xs text-zinc-500">
            {item.database} · {item.durationSeconds} 秒
          </p>
        </div>
        {item.rollbackAvailable && (
          <button className="inline-flex items-center gap-1 rounded border border-zinc-300 px-3 py-2 text-sm font-semibold">
            <RotateCcw className="h-4 w-4" />
            回復版本
          </button>
        )}
      </div>
      <div className="pt-4">
        <Steps steps={item.steps} />
      </div>
    </article>
  );
}

function Migrations({
  data,
  environment,
}: {
  data: DemoData;
  environment: Environment;
}) {
  const items = filterByEnvironment(data.migrations, environment);
  return (
    <>
      <SectionHeader
        title="資料庫遷移"
        subtitle="執行前檢查、模擬執行、套用、驗證與回復準備狀態。"
      />
      {items.length ? (
        <div className="space-y-3">
          {items.map((item) => (
            <MigrationCard key={item.id} item={item} />
          ))}
        </div>
      ) : (
        <EmptyState
          title="沒有遷移紀錄"
          message="目前沒有符合此環境的資料庫遷移紀錄。"
        />
      )}
    </>
  );
}

export function App() {
  const [route, setRoute] = useState(getRoute);
  const [scenario, setScenario] = useState<Scenario>("mixed");
  const [environment, setEnvironment] = useState<Environment>("All");
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<DemoData>(() => createDemoRepository());
  const [error, setError] = useState("");
  useEffect(() => {
    const change = () => setRoute(getRoute());
    window.addEventListener("hashchange", change);
    return () => window.removeEventListener("hashchange", change);
  }, []);
  const changeScenario = (next: Scenario) => {
    setScenario(next);
    setLoading(true);
    setError("");
    window.setTimeout(() => {
      try {
        setData(createDemoRepository({ scenario: next }));
      } catch (reason) {
        setError(
          reason instanceof Error ? reason.message : "未知的資料來源錯誤",
        );
      } finally {
        setLoading(false);
      }
    }, 250);
  };
  const content = () => {
    if (error)
      return (
        <div className="panel flex min-h-72 flex-col items-center justify-center px-6 text-center">
          <AlertTriangle className="h-10 w-10 text-red-600" />
          <h1 className="mt-3 text-lg font-bold">示範資料來源無法使用</h1>
          <p className="mt-1 max-w-lg text-sm text-zinc-500">{error}</p>
          <button
            onClick={() => changeScenario("mixed")}
            className="mt-4 inline-flex items-center gap-2 rounded bg-zinc-900 px-4 py-2 text-sm font-semibold text-white"
          >
            <RefreshCw className="h-4 w-4" />
            載入預設情境
          </button>
        </div>
      );
    if (loading)
      return (
        <div aria-label="正在載入示範資料" className="grid animate-pulse gap-3">
          <div className="h-16 bg-zinc-200" />
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="h-28 bg-zinc-200" />
            ))}
          </div>
          <div className="h-72 bg-zinc-200" />
        </div>
      );
    switch (route.page) {
      case "instances":
        return (
          <Instances
            data={data}
            environment={environment}
            detailId={route.detailId}
          />
        );
      case "incidents":
        return (
          <Incidents
            key={data.scenario}
            data={data}
            environment={environment}
          />
        );
      case "deployments":
        return <Deployments data={data} />;
      case "migrations":
        return <Migrations data={data} environment={environment} />;
      default:
        return <Dashboard data={data} environment={environment} />;
    }
  };
  return (
    <div className="min-h-screen bg-[#f4f6f8] text-zinc-800">
      <aside className="fixed inset-y-0 left-0 z-20 hidden w-56 bg-[#182724] text-zinc-100 lg:block">
        <div className="flex h-16 items-center gap-2 border-b border-white/10 px-5">
          <Boxes className="h-6 w-6 text-emerald-400" />
          <div>
            <p className="text-sm font-bold">DBAIOps</p>
            <p className="text-[10px] uppercase text-zinc-400">交付控制台</p>
          </div>
        </div>
        <nav aria-label="主要導覽" className="p-3">
          {navigation.map(({ page, label, icon: Icon }) => (
            <a
              key={page}
              href={`#/${page}`}
              className={`mb-1 flex items-center gap-3 rounded px-3 py-2.5 text-sm ${route.page === page ? "bg-emerald-500/15 font-semibold text-emerald-300" : "text-zinc-300 hover:bg-white/5"}`}
            >
              <Icon className="h-4 w-4" />
              {label}
            </a>
          ))}
        </nav>
        <div className="absolute inset-x-4 bottom-4 border-t border-white/10 pt-4 text-xs text-zinc-400">
          <span className="rounded border border-emerald-700 bg-emerald-950 px-2 py-1 font-semibold text-emerald-300">
            示範 · 模擬資料
          </span>
          <p className="mt-3">資料種子 1043130</p>
          <p className="mt-1 truncate" title={buildMetadata.sha}>
            {buildMetadata.version} · {buildMetadata.sha.slice(0, 8)}
          </p>
          <p className="mt-1 truncate" title={buildMetadata.time}>
            {buildEnvironmentLabels[buildMetadata.environment] ??
              buildMetadata.environment}
          </p>
        </div>
      </aside>
      <div className="lg:pl-56">
        <header className="sticky top-0 z-10 border-b border-zinc-200 bg-white/95 px-4 py-3 backdrop-blur md:px-6">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <div className="flex items-center gap-2">
                <p className="text-xs font-semibold uppercase text-zinc-400">
                  維運工作區
                </p>
                <span className="rounded border border-emerald-200 bg-emerald-50 px-1.5 py-0.5 text-[9px] font-bold text-emerald-800 lg:hidden">
                  示範 · 模擬資料
                </span>
              </div>
              <p className="text-sm font-semibold">
                {navigation.find((item) => item.page === route.page)?.label}
              </p>
            </div>
            <div className="flex min-w-0 flex-wrap items-center gap-2">
              <select
                aria-label="示範情境"
                value={scenario}
                onChange={(e) => changeScenario(e.target.value as Scenario)}
                className="max-w-48 rounded border border-zinc-300 bg-white px-3 py-2 text-sm"
              >
                {scenarios.map((item) => (
                  <option key={item.value} value={item.value}>
                    {item.label}
                  </option>
                ))}
              </select>
              <select
                aria-label="環境"
                value={environment}
                onChange={(e) => setEnvironment(e.target.value as Environment)}
                className="rounded border border-zinc-300 bg-white px-3 py-2 text-sm"
              >
                <option value="All">全部環境</option>
                <option value="Dev">開發</option>
                <option value="Staging">預備</option>
                <option value="Production">正式</option>
              </select>
              <button
                title="重設示範資料"
                aria-label="重設示範資料"
                onClick={() => changeScenario("mixed")}
                className="rounded border border-zinc-300 bg-white p-2"
              >
                <RefreshCw className="h-4 w-4" />
              </button>
            </div>
          </div>
        </header>
        <main className="mx-auto max-w-[1600px] p-4 pb-24 md:p-6 lg:pb-6">
          {content()}
        </main>
      </div>
      <nav
        aria-label="行動版主要導覽"
        className="fixed inset-x-0 bottom-0 z-30 grid grid-cols-5 border-t border-zinc-200 bg-white lg:hidden"
      >
        {navigation.map(({ page, label, icon: Icon }) => (
          <a
            key={page}
            href={`#/${page}`}
            className={`flex h-16 min-w-0 flex-col items-center justify-center gap-1 text-[10px] ${route.page === page ? "font-semibold text-emerald-700" : "text-zinc-500"}`}
          >
            <Icon className="h-5 w-5" />
            <span className="truncate">{label}</span>
          </a>
        ))}
      </nav>
    </div>
  );
}
